import { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Search, Pencil, Plus, ChevronDown, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';

const GEO_OPTIONS = [
  { value: 'nyc', label: 'NYC', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  { value: 'nassau_county', label: 'Nassau County', color: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  { value: 'suffolk_county', label: 'Suffolk County', color: 'bg-teal-500/15 text-teal-500 border-teal-500/30' },
  { value: 'westchester', label: 'Westchester', color: 'bg-purple-500/15 text-purple-500 border-purple-500/30' },
  { value: 'universal', label: 'Universal', color: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
];
const GEO_LABEL = Object.fromEntries(GEO_OPTIONS.map(o => [o.value, o.label]));
const GEO_COLOR = Object.fromEntries(GEO_OPTIONS.map(o => [o.value, o.color]));

const GROUP_OPTIONS = [
  { value: 'nyc_dob', label: 'NYC DOB' },
  { value: 'nyc_other', label: 'NYC Other Agencies' },
  { value: 'nassau_county', label: 'Nassau County' },
  { value: 'suffolk_county', label: 'Suffolk County' },
  { value: 'westchester', label: 'Westchester' },
  { value: 'universal', label: 'Universal' },
];
const GROUP_LABEL = Object.fromEntries(GROUP_OPTIONS.map(o => [o.value, o.label]));

const DEFAULT_PHASE_LABELS = [
  'Phase 1: Application Preparation',
  'Phase 2: Agency Coordination & Review',
  'Phase 3: Permitting',
  'Phase 4: Closeout & Signoff',
];

// Multi-select geography as toggle pills.
function GeoMultiSelect({ value, onChange, testIdPrefix }) {
  const toggle = (g) => {
    if (value.includes(g)) {
      onChange(value.filter(v => v !== g));
    } else {
      onChange([...value, g]);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {GEO_OPTIONS.map(opt => {
        const active = value.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => toggle(opt.value)}
            className={`px-2.5 py-1 text-xs rounded-[4px] border transition-colors ${
              active ? opt.color : 'border-border bg-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`${testIdPrefix}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function formatMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '';
  const num = Number(n);
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// One-row section list item; clicking expands, pencil icon opens edit sheet.
function SectionRow({ section, isExpanded, onExpandToggle, onEdit, canEdit, showInactiveBadge }) {
  const feeMissing = !section.is_hourly && (section.expected_total_fee === null || section.expected_total_fee === undefined);
  const hoursMissing = section.expected_hours === null || section.expected_hours === undefined;
  return (
    <div
      className="border border-border rounded-[4px] bg-card hover:bg-secondary/20 transition-colors"
      data-testid={`section-row-${section.service_code}`}
    >
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
        onClick={onExpandToggle}
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <Badge variant="outline" className="text-[11px] font-mono border-border bg-transparent">{section.service_code}</Badge>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{section.name}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            {section.agency && <span>{section.agency}</span>}
            {section.agency && <span className="opacity-40">-</span>}
            <span>{section.is_hourly ? `Hourly @ ${formatMoney(section.hourly_rate)}` : `${section.phase_count} phase${section.phase_count === 1 ? '' : 's'}`}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {(section.geography || []).map(g => (
            <span key={g} className={`text-[10px] px-1.5 py-0.5 rounded-[4px] border ${GEO_COLOR[g] || ''}`}>
              {GEO_LABEL[g] || g}
            </span>
          ))}
        </div>
        <div className="w-28 text-right text-xs">
          {section.is_hourly ? (
            <span className="text-muted-foreground">-</span>
          ) : feeMissing ? (
            <span className="text-amber-500 text-[10px] px-1.5 py-0.5 rounded-[4px] bg-amber-500/10 border border-amber-500/30">Fee not set</span>
          ) : (
            <span className="font-mono">{formatMoney(section.expected_total_fee)}</span>
          )}
        </div>
        <div className="w-24 text-right text-xs">
          {hoursMissing ? (
            <span className="text-amber-500 text-[10px] px-1.5 py-0.5 rounded-[4px] bg-amber-500/10 border border-amber-500/30">Hours not set</span>
          ) : (
            <span className="font-mono">{section.expected_hours}h</span>
          )}
        </div>
        {showInactiveBadge && !section.is_active && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 inline-block" /> Inactive
          </span>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1.5 rounded-[4px] hover:bg-secondary text-muted-foreground hover:text-foreground"
            data-testid={`section-edit-${section.service_code}`}
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="border-t border-border px-3 py-3 space-y-3 bg-secondary/10" data-testid={`section-expanded-${section.service_code}`}>
          {section.is_hourly ? (
            <div className="text-xs">
              <div className="font-medium mb-1">Hourly Rate</div>
              <div className="text-muted-foreground">{formatMoney(section.hourly_rate)} per hour</div>
            </div>
          ) : (
            (section.phases || []).map((p) => (
              <div key={p.phase_number} className="border-l-2 border-border pl-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-medium">{p.label}</div>
                  <div className="text-xs font-mono">
                    {p.expected_fee === null || p.expected_fee === undefined ? (
                      <span className="text-amber-500 text-[10px]">Fee not set</span>
                    ) : formatMoney(p.expected_fee)}
                  </div>
                </div>
                <RichTextDisplay value={p.scope_text} data-testid={`section-phase-scope-${section.service_code}-${p.phase_number}`} />
              </div>
            ))
          )}
          {section.notes && (
            <div className="pt-2 border-t border-border/50">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Internal Notes</div>
              <div className="text-xs text-muted-foreground whitespace-pre-wrap">{section.notes}</div>
            </div>
          )}
          {canEdit && (
            <div className="pt-2 flex justify-end">
              <Button size="sm" variant="outline" className="text-xs h-8 border-border" onClick={onEdit} data-testid={`section-edit-btn-${section.service_code}`}>
                <Pencil className="h-3 w-3 mr-1" /> Edit
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Create / edit sheet
function SectionEditSheet({ open, onOpenChange, section, isCreate, userRole, onSaved, triggerRef }) {
  const isAdmin = userRole === 'Administrator';
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextCode, setNextCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  // Defer heavy RichTextEditor mounting until the Sheet's slide-in animation
  // has settled. This avoids a React "concurrent rendering recovered" warning
  // caused by Quill's synchronous DOM mutation racing StrictMode's double-invoke.
  const [editorsReady, setEditorsReady] = useState(0); // counter: how many editors may mount

  useEffect(() => {
    if (!open) {
      setEditorsReady(0);
      return;
    }
    // Stagger editor mounts to avoid a React concurrent-rendering recover
    // caused by Quill's synchronous DOM setup racing StrictMode's double-invoke.
    // Mount +1 editor every 100ms, up to 8 (safe upper bound for phase count).
    const timers = [];
    for (let i = 0; i < 8; i++) {
      timers.push(setTimeout(() => setEditorsReady(n => Math.max(n, i + 1)), 220 + i * 100));
    }
    return () => timers.forEach(clearTimeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setForm({
        name: '',
        geography: [],
        agency: '',
        group: 'nyc_dob',
        is_hourly: false,
        hourly_rate: 150,
        phases: [
          { phase_number: 1, label: DEFAULT_PHASE_LABELS[0], scope_text: '', expected_fee: null },
        ],
        expected_hours: null,
        notes: '',
        is_active: true,
      });
      setNextCode('');
      setCodeError('');
    } else if (section) {
      setForm({
        service_code: section.service_code,
        name: section.name || '',
        geography: [...(section.geography || [])],
        agency: section.agency || '',
        group: section.group || 'nyc_dob',
        is_hourly: !!section.is_hourly,
        hourly_rate: section.hourly_rate ?? 150,
        phases: (section.phases || []).map(p => ({ ...p })),
        expected_hours: section.expected_hours ?? null,
        notes: section.notes || '',
        is_active: section.is_active !== false,
      });
      setNextCode(section.service_code);
      setCodeError('');
    }
  }, [open, isCreate, section]);

  // In create mode the service code is derived from the selected geography
  // (jurisdiction-prefixed: NYC-/LI-/WC-). Refresh the preview whenever the
  // geography selection changes.
  const geoKey = (form?.geography || []).join(',');
  useEffect(() => {
    if (!open || !isCreate || !form) return;
    const geo = form.geography || [];
    if (geo.length === 0) { setNextCode(''); setCodeError(''); return; }
    api.get('/section-library/next-code', { params: { geography: geo.join(',') } })
      .then(r => { setNextCode(r.data.next_code || ''); setCodeError(''); })
      .catch(e => { setNextCode(''); setCodeError(e?.response?.data?.detail || ''); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreate, geoKey]);

  if (!form) return null;

  const setPhaseCount = (n) => {
    const cur = form.phases;
    if (n === cur.length) return;
    if (n > cur.length) {
      const add = [];
      for (let i = cur.length; i < n; i++) {
        add.push({
          phase_number: i + 1,
          label: DEFAULT_PHASE_LABELS[i] || `Phase ${i + 1}`,
          scope_text: '',
          expected_fee: null,
        });
      }
      setForm({ ...form, phases: [...cur, ...add] });
    } else {
      setForm({ ...form, phases: cur.slice(0, n).map((p, i) => ({ ...p, phase_number: i + 1 })) });
    }
  };

  const updatePhase = (idx, patch) => {
    const next = form.phases.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    setForm({ ...form, phases: next });
  };

  const computedTotal = form.is_hourly
    ? null
    : form.phases.reduce((acc, p) => {
      if (acc === null) return null;
      if (p.expected_fee === null || p.expected_fee === undefined || p.expected_fee === '') return null;
      const v = Number(p.expected_fee);
      return Number.isNaN(v) ? null : acc + v;
    }, 0);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.geography.length === 0) { toast.error('At least one geography is required'); return; }
    if (form.is_hourly && (!form.hourly_rate || form.hourly_rate <= 0)) { toast.error('Hourly rate must be greater than zero'); return; }
    if (!form.is_hourly && form.phases.length === 0) { toast.error('At least one phase is required'); return; }

    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        geography: form.geography,
        agency: form.agency.trim(),
        group: form.group,
        is_hourly: form.is_hourly,
        hourly_rate: form.is_hourly ? Number(form.hourly_rate) : null,
        phases: form.is_hourly ? [] : form.phases.map((p, i) => ({
          phase_number: i + 1,
          label: (p.label || '').trim(),
          scope_text: p.scope_text || '',
          expected_fee: (p.expected_fee === null || p.expected_fee === undefined || p.expected_fee === '') ? null : Number(p.expected_fee),
        })),
        expected_hours: (form.expected_hours === null || form.expected_hours === undefined || form.expected_hours === '') ? null : Number(form.expected_hours),
        notes: form.notes || '',
        is_active: form.is_active,
      };
      if (isCreate) {
        const res = await api.post('/section-library', body);
        toast.success(`Section ${res?.data?.service_code || ''} created`);
      } else {
        if (isAdmin && form.service_code && form.service_code !== section.service_code) {
          body.service_code = form.service_code.trim();
        }
        await api.put(`/section-library/${section.id}`, body);
        toast.success('Section updated');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(msg);
      if (msg && msg.includes('service_code')) setCodeError(msg);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    try {
      await api.post(`/section-library/${section.id}/toggle-active`);
      toast.success(section.is_active ? 'Section deactivated' : 'Section reactivated');
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to toggle active status');
    } finally {
      setConfirmDeactivate(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="section-edit-sheet" onCloseAutoFocus={(e) => { if (triggerRef?.current && document.body.contains(triggerRef.current)) { e.preventDefault(); triggerRef.current.focus(); } }}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span>{isCreate ? 'New Section' : 'Edit Section'}</span>
            {isAdmin && !isCreate ? (
              <div>
                <Input
                  value={form.service_code || ''}
                  onChange={(e) => { setForm({ ...form, service_code: e.target.value }); setCodeError(''); }}
                  className="h-7 text-[11px] font-mono w-28 bg-transparent border-border"
                  aria-label="Service code" data-testid="section-edit-service-code"
                />
                {codeError && <div className="text-[10px] text-destructive mt-1">{codeError}</div>}
              </div>
            ) : (
              <Badge variant="outline" className="text-[11px] font-mono border-border" data-testid="section-edit-service-code-badge">
                {isCreate ? (nextCode || 'Auto-assigned') : form.service_code}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {isCreate ? 'Create a new scope section for the proposal library.' : 'Edit an existing scope section.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {/* Identity */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 bg-transparent border-border"
                aria-label="Section name" data-testid="section-edit-name"
              />
            </div>

            <div>
              <Label className="text-xs">Geography *</Label>
              <div className="mt-1">
                <GeoMultiSelect
                  value={form.geography}
                  onChange={(v) => setForm({ ...form, geography: v })}
                  testIdPrefix="section-edit-geo"
                />
              </div>
              {isCreate && codeError && (
                <div className="text-[10px] text-amber-500 mt-1.5" data-testid="section-edit-geo-code-error">{codeError}</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Agency</Label>
                <Input
                  value={form.agency}
                  onChange={(e) => setForm({ ...form, agency: e.target.value })}
                  className="mt-1 bg-transparent border-border"
                  aria-label="Agency" data-testid="section-edit-agency"
                />
              </div>
              <div>
                <Label className="text-xs">Group *</Label>
                <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                  <SelectTrigger className="mt-1 bg-transparent border-border h-9 text-xs" aria-label="Group" data-testid="section-edit-group">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_OPTIONS.map(g => (
                      <SelectItem key={g.value} value={g.value} className="text-xs">{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <Label className="text-xs">Is Hourly</Label>
                <div className="text-[11px] text-muted-foreground">Hourly sections have no phase structure.</div>
              </div>
              <Switch
                checked={form.is_hourly}
                onCheckedChange={(v) => setForm({ ...form, is_hourly: v })}
                aria-label="Is hourly" data-testid="section-edit-hourly-toggle"
              />
            </div>

            {form.is_hourly && (
              <div>
                <Label className="text-xs">Hourly Rate ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.hourly_rate ?? ''}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  className="mt-1 bg-transparent border-border"
                  aria-label="Hourly rate" data-testid="section-edit-hourly-rate"
                />
              </div>
            )}
          </div>

          {/* Phase builder */}
          {!form.is_hourly && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center gap-3">
                <Label className="text-xs">Phase Count</Label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPhaseCount(n)}
                      className={`w-8 h-7 text-xs rounded-[4px] border ${
                        form.phases.length === n
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`section-edit-phase-count-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {form.phases.map((p, idx) => (
                <div key={idx} className="border border-border rounded-[4px] p-3 space-y-2">
                  <div className="grid grid-cols-[1fr_140px] gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Phase {idx + 1} Label</Label>
                      <Input
                        value={p.label}
                        onChange={(e) => updatePhase(idx, { label: e.target.value })}
                        className="mt-1 bg-transparent border-border h-8 text-xs"
                        aria-label="Phase label" data-testid={`section-edit-phase-${idx}-label`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Expected Fee ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Not set"
                        value={p.expected_fee ?? ''}
                        onChange={(e) => updatePhase(idx, { expected_fee: e.target.value === '' ? null : e.target.value })}
                        className={`mt-1 bg-transparent h-8 text-xs ${(p.expected_fee === null || p.expected_fee === undefined || p.expected_fee === '') ? 'border-amber-500/60' : 'border-border'}`}
                        aria-label="Expected fee" data-testid={`section-edit-phase-${idx}-fee`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Scope Text</Label>
                    <div className="mt-1" data-testid={`section-edit-phase-${idx}-scope`}>
                      {editorsReady > idx ? (
                        <RichTextEditor
                          value={p.scope_text}
                          onChange={(v) => updatePhase(idx, { scope_text: v })}
                          placeholder="Enter scope text for this phase..."
                        />
                      ) : (
                        <div className="border border-border rounded-[4px] px-3 py-8 text-center text-[11px] text-muted-foreground bg-secondary/10">
                          Loading editor...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between border border-border rounded-[4px] px-3 py-2 bg-secondary/20">
                <span className="text-xs text-muted-foreground">Expected Total Fee</span>
                <span className={`text-sm font-mono ${computedTotal === null ? 'text-amber-500' : ''}`} data-testid="section-edit-computed-total">
                  {computedTotal === null ? 'Incomplete' : formatMoney(computedTotal)}
                </span>
              </div>
            </div>
          )}

          {/* Hours */}
          <div className="pt-2 border-t border-border">
            <Label className="text-xs">Expected Total Hours</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              placeholder="Not set"
              value={form.expected_hours ?? ''}
              onChange={(e) => setForm({ ...form, expected_hours: e.target.value === '' ? null : e.target.value })}
              className={`mt-1 bg-transparent ${(form.expected_hours === null || form.expected_hours === undefined || form.expected_hours === '') ? 'border-amber-500/60' : 'border-border'}`}
              aria-label="Expected total hours" data-testid="section-edit-hours"
            />
            {(form.expected_hours === null || form.expected_hours === undefined || form.expected_hours === '') && (
              <div className="text-[10px] text-amber-500 mt-1">Required for financial analysis</div>
            )}
          </div>

          {/* Notes */}
          <div className="pt-2 border-t border-border">
            <Label className="text-xs">Internal Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder="Not shown on proposals."
              className="mt-1 bg-transparent border-border text-xs"
              aria-label="Internal notes" data-testid="section-edit-notes"
            />
          </div>

          {/* Active toggle (edit only) */}
          {!isCreate && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <Label className="text-xs">Active</Label>
                <div className="text-[11px] text-muted-foreground">Inactive sections are hidden from proposal pickers.</div>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                aria-label="Active" data-testid="section-edit-active-toggle"
              />
            </div>
          )}
        </div>

        <SheetFooter className="mt-6 flex sm:justify-between gap-2">
          {!isCreate && form.is_active && (
            <Button
              variant="outline"
              className="text-xs h-9 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setConfirmDeactivate(true)}
              data-testid="section-edit-deactivate-btn"
            >
              <X className="h-3 w-3 mr-1" /> Deactivate
            </Button>
          )}
          {!isCreate && !form.is_active && (
            <span className="text-[11px] text-muted-foreground self-center">Section is currently inactive</span>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" className="text-xs h-9 border-border" onClick={() => onOpenChange(false)} data-testid="section-edit-cancel-btn">Cancel</Button>
            <Button className="text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving} data-testid="section-edit-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </SheetFooter>

        <AlertDialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
          <AlertDialogContent data-testid="section-edit-deactivate-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate this section?</AlertDialogTitle>
              <AlertDialogDescription>
                It will no longer appear in proposal section pickers, but stays visible under the Inactive filter.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deactivate} className="bg-destructive text-destructive-foreground">Deactivate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

export default function ScopeSectionsTab({ canEdit, incompleteFilter, onIncompleteFilterChange, onDataChanged, userRole }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [geoFilter, setGeoFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [expanded, setExpanded] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSection, setSheetSection] = useState(null);
  const [sheetIsCreate, setSheetIsCreate] = useState(false);
  const triggerElRef = useRef(null);

  const load = () => {
    setLoading(true);
    // Backend accepts is_active values: "true" | "false" | "all"
    // FE state uses "active" | "inactive" | "all" - translate before send.
    const statusMap = { active: 'true', inactive: 'false', all: 'all' };
    const params = { is_active: statusMap[statusFilter] || 'true' };
    api.get('/section-library', { params }).then(r => {
      setSections(r.data);
    }).catch(() => toast.error('Failed to load sections')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    let list = sections;
    if (geoFilter !== 'all') list = list.filter(s => (s.geography || []).includes(geoFilter));
    if (groupFilter !== 'all') list = list.filter(s => s.group === groupFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(x =>
        (x.name || '').toLowerCase().includes(s)
        || (x.service_code || '').toLowerCase().includes(s)
      );
    }
    if (incompleteFilter) {
      list = list.filter(x => {
        const feeMissing = !x.is_hourly && (x.expected_total_fee === null || x.expected_total_fee === undefined);
        const hoursMissing = x.expected_hours === null || x.expected_hours === undefined;
        return feeMissing || hoursMissing;
      });
    }
    return list;
  }, [sections, geoFilter, groupFilter, search, incompleteFilter]);

  const openCreate = () => {
    triggerElRef.current = document.activeElement;
    setSheetSection(null);
    setSheetIsCreate(true);
    setSheetOpen(true);
  };
  const openEdit = (sec) => {
    triggerElRef.current = document.activeElement;
    setSheetSection(sec);
    setSheetIsCreate(false);
    setSheetOpen(true);
  };

  const handleSaved = () => {
    load();
    onDataChanged?.();
  };

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sections..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-card border-border w-56"
            data-testid="sections-search"
          />
        </div>
        <Select value={geoFilter} onValueChange={setGeoFilter}>
          <SelectTrigger className="w-40 bg-card border-border text-xs h-9" aria-label="Filter by geography" data-testid="sections-geo-filter">
            <SelectValue placeholder="All Geographies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Geographies</SelectItem>
            {GEO_OPTIONS.map(g => (
              <SelectItem key={g.value} value={g.value} className="text-xs">{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-44 bg-card border-border text-xs h-9" aria-label="Filter by group" data-testid="sections-group-filter">
            <SelectValue placeholder="All Groups" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Groups</SelectItem>
            {GROUP_OPTIONS.map(g => (
              <SelectItem key={g.value} value={g.value} className="text-xs">{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 bg-card border-border text-xs h-9" aria-label="Filter by status" data-testid="sections-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active" className="text-xs">Active</SelectItem>
            <SelectItem value="inactive" className="text-xs">Inactive</SelectItem>
            <SelectItem value="all" className="text-xs">All</SelectItem>
          </SelectContent>
        </Select>

        {incompleteFilter && (
          <button
            className="text-xs text-primary hover:underline ml-1"
            onClick={() => onIncompleteFilterChange?.(false)}
            data-testid="sections-clear-incomplete"
          >
            Clear &quot;Incomplete only&quot;
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground tabular-nums">{filtered.length} of {sections.length}</span>
          {canEdit && (
            <Button
              size="sm"
              className="text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={openCreate}
              data-testid="sections-add-btn"
            >
              <Plus className="h-3 w-3 mr-1" /> Add Section
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <Card className="border-border bg-card shadow-none"><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-10 text-center text-xs text-muted-foreground" data-testid="sections-empty">
            {sections.length === 0
              ? 'No sections found. Add the first section using the button above.'
              : 'No sections match your filters.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5" data-testid="sections-list">
          {filtered.map(s => (
            <SectionRow
              key={s.id}
              section={s}
              isExpanded={!!expanded[s.id]}
              onExpandToggle={() => setExpanded(prev => ({ ...prev, [s.id]: !prev[s.id] }))}
              onEdit={() => openEdit(s)}
              canEdit={canEdit}
              showInactiveBadge={statusFilter !== 'active'}
            />
          ))}
        </div>
      )}

      <SectionEditSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        section={sheetSection}
        isCreate={sheetIsCreate}
        userRole={userRole}
        onSaved={handleSaved}
        triggerRef={triggerElRef}
      />
    </div>
  );
}
